import React, { useEffect, useRef, useState } from "react";
import { HotkeysProvider, OverlaysProvider } from "@blueprintjs/core";
import { RouterProvider, createBrowserRouter } from "react-router-dom";
import { ApplicationStoreType } from "modules/root/models/store";
import { Notifications } from "./Notifications";
import { AuthPopup } from "modules/session/auth/components/AuthPopup";
import styles from "./App.module.scss";
import { observer } from "mobx-react";
import { RootStoreProvider } from "modules/root/models/hooks";
import { InvalidVersion } from "./InvalidVersion";
import GeneralIcons from "modules/root/styles/planr/general.svg?react";
import StartPopup from "modules/root/components/StartPage/StartPopup";
import { AppShell, ShellProvider } from "./AppShell";

const App: React.FC<AppProps> = (props) => {
  const { store, onLogout } = props;
  const isMobileWidth = () => window.innerWidth < 885;
  const [hideMenu, setHideMenu] = useState(isMobileWidth);
  const [isMobile, setIsMobile] = useState(isMobileWidth);
  const prevMobile = useRef(isMobile);

  function handleResize() {
    const mobile = isMobileWidth();
    // реагируем только на переход через порог 885px, чтобы не сбрасывать
    // состояние «открыто/закрыто» при каждом ресайзе или повороте экрана
    if (prevMobile.current !== mobile) {
      prevMobile.current = mobile;
      setIsMobile(mobile);
      setHideMenu(mobile);
    }
  }

  useEffect(() => {
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // мобильные оверлеи: выехавшее меню и панель списка проектов
  const mobileMenuOpen = isMobile && !hideMenu;
  const orderListVisible = isMobile && store.orders.list.visible;
  const overlayVisible = mobileMenuOpen || orderListVisible;

  const closeOverlays = () => {
    setHideMenu(true);
    store.orders.list.toggleVisibility(false);
  };

  // пока открыт любой мобильный оверлей — блокируем прокрутку фона и закрываем по Esc
  useEffect(() => {
    if (!overlayVisible) {
      return undefined;
    }

    document.body.classList.add("mobile-menu-open");
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closeOverlays();
      }
    };
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.classList.remove("mobile-menu-open");
      window.removeEventListener("keydown", onKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlayVisible]);
  const { loggedIn, authPopupVisible } = store.session;
  const versionMismatch = store.apiVersion && store.apiVersion !== store.uiVerson;
  const startInfoFilled = store.startInfoFilled;
  const resetState = (successfull: boolean) => {
    const { store } = props;
    store.setStartupInfoState(successfull);
  };

  const invalidVersionVisible = !authPopupVisible && versionMismatch;
  const startSettingsVisible = !authPopupVisible && !invalidVersionVisible && loggedIn && !startInfoFilled;

  const mainContentVisible = !authPopupVisible && !invalidVersionVisible && !startSettingsVisible;

  // Роутер создаётся один раз: он держит историю переходов, и пересоздание
  // сбрасывало бы её. Всё меняющееся приходит в каркас через ShellContext,
  // а маршруты собираются на каждом рендере внутри самого каркаса.
  //
  // Инициализатор useState, а не useMemo: createBrowserRouter внутри сразу
  // подписывается на popstate, то есть это побочный эффект, а не вычисление.
  // useMemo React вправе выполнить повторно (и выполнит под StrictMode), и тогда
  // осталась бы вторая живая подписка, которую никто не рисует и не закрывает.
  const [router] = useState(() => createBrowserRouter([{ path: "*", element: <AppShell /> }]));

  const shell = {
    hideMenu,
    setHideMenu,
    isMobile,
    overlayVisible,
    orderListVisible,
    closeOverlays,
    onLogout,
  };

  return (
    <RootStoreProvider value={store}>
      {/* Провайдер горячих клавиш нужен хуку useHotkeys: без него Blueprint 5
          пишет в консоль предупреждение и не собирает список сочетаний */}
      <HotkeysProvider>
        {/* Провайдер стека оверлеев: на нём держатся Popover, Dialog, Drawer
            и тостер. Без него Blueprint 5 работает через устаревший модульный
            стек и ругается в консоль, а в шестой версии это перестанет работать */}
        <OverlaysProvider>
        <GeneralIcons className="hidden" />
        <div className={styles.mainlayout}>
          {authPopupVisible && <AuthPopup store={store.session} />}
          {startSettingsVisible && (
            <>
              <StartPopup
                store={store.startSettings}
                onLogout={onLogout}
                access={store.session.access}
                onSaved={resetState}
                session={store.session}
              />
              <Notifications />
            </>
          )}
          {invalidVersionVisible && <InvalidVersion />}
          {mainContentVisible && (
            <ShellProvider value={shell}>
              {/* переходы — через startTransition, как станет по умолчанию в v7 */}
              <RouterProvider router={router} future={{ v7_startTransition: true }} />
            </ShellProvider>
          )}
        </div>
        </OverlaysProvider>
      </HotkeysProvider>
    </RootStoreProvider>
  );
};

export default observer(App);

export const ArrowEvents = {
  arrow: "rost.event.arrow",
};

interface AppState {
  hideMenu: boolean;
  width: number;
}

interface AppProps {
  store: ApplicationStoreType;
  onLogout: () => void;
}
